'use client';

import {
    Footer as UdemyFooter,
    mapMonolithDataToFooterProps,
    FooterClientData,
    FooterConfig,
} from '@udemy-v2/react-footer';
import {useFooterData} from '@/lib/hooks/use-footer-data';
import {useCommonData} from '@udemy-v2/common-data';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {UBLinkPath, useUdemyBusinessLink} from '@udemy-v2/organization-growth';
import {OrganizationType} from '@udemy-v2/graphql';

// Mirrors the default feature flag values from udemy/features.py
const MARKETPLACE_FEATURES: Record<string, boolean> = {
    'footer.links.about': true,
    'footer.links.business': true,
    'footer.links.teach': true,
    'footer.links.affiliates': true,
    'footer.links.blog': true,
    'footer.links.careers': true,
    'footer.links.contact': true,
    'footer.links.impressum_contact': true,
    'footer.links.benesse_corporate_page': false,
    'footer.links.commercial_transactions_law_page': true,
};

// Flags with organization_fallback=False in Django features.py
const UB_FEATURE_OVERRIDES: Record<string, boolean> = {
    'footer.links.business': false,
    'footer.links.teach': false,
    'footer.links.affiliates': false,
    'footer.links.blog': false,
    'footer.links.careers': false,
    'footer.links.contact': false,
};

const buildFeatures = (hasOrganization: boolean): Record<string, boolean> => ({
    ...MARKETPLACE_FEATURES,
    ...(hasOrganization ? UB_FEATURE_OVERRIDES : {}),
});

export const Footer = () => {
    const footerData = useFooterData();
    const commonData = useCommonData();
    const {featureVariantAssignments} = useFeatures(['browse_aggregator_page', 'career_academies']);

    const ubDemoUrl = useUdemyBusinessLink(UBLinkPath.REQUEST_DEMO_MX, {
        ref: 'footer-ad',
        user_type: 'mx',
    });

    if (!footerData) {
        return null;
    }

    const hasOrganization = Boolean(commonData?.organization);

    // The client-side ExperimentationProvider omits realtimeAttributes, so these
    // experiments resolve to true even for org users. The monolith excludes them
    // via CSRequestContext — the !hasOrganization guard replicates that behavior.
    const isAggregatorEnabled =
        !hasOrganization &&
        !!featureVariantAssignments.get('browse_aggregator_page')?.configuration?.pageEnabled;

    const isAcademiesEnabled =
        !hasOrganization &&
        !!featureVariantAssignments.get('career_academies')?.configuration?.feature;

    const clientData: FooterClientData = {
        user: commonData?.user
            ? {
                  has_instructor_intent: !!commonData.user.isInstructor,
              }
            : null,
        footerExperimentFeatures: {
            isAggregatorEnabled,
            isAcademiesEnabled,
        },
    };

    const configData: Partial<FooterConfig> = {
        brand: {
            has_organization: hasOrganization,
            organization: {
                is_enterprise_china:
                    commonData?.organization?.type === OrganizationType.UdemyBusinessChina,
                is_limited_consumption_trial: Boolean(
                    Number(commonData?.user?.subscription?.trialInterval?.count) > 0,
                ),
            },
        },
        marketplace_country: {
            id: commonData?.user?.country ?? 'US',
        },
        features: buildFeatures(hasOrganization),
    };

    const footerProps = mapMonolithDataToFooterProps(footerData, clientData, configData);

    return <UdemyFooter {...footerProps} ufbNoticeLink={ubDemoUrl} />;
};
